import { Component, OnInit, ViewChild } from '@angular/core';
import * as fromMembers from '../../reducers/members.reducer';
import * as fromRoot from '@app/reducers';
import { SmashInputFileComponent } from '@app/shared/modules/smash-forms/smash-input-file/smash-input-file.component';
import { Identity } from '@app/shared/models/identity';
import { User } from '@app/shared/models/user';
import { DialogRef } from '@ngneat/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ExportToCsv } from 'export-to-csv';
import { Observable, Subscription } from 'rxjs';
import { first, take } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { MembersActions } from '../../actions';

@Component({
  selector: 'app-modal-import-members',
  templateUrl: './modal-import-members.component.html',
  styleUrls: ['./modal-import-members.component.scss']
})
export class ModalImportMembersComponent implements OnInit {

  @ViewChild('inputFile') inputFile: SmashInputFileComponent;

  identity$: Observable<Identity>;
  importCSVProcessing$: Observable<boolean>;
  checkContentFileProcessing$: Observable<boolean>;
  checkContentFileError$: Observable<any>;
  importMembersProcessing$: Observable<boolean>;
  importMembersSuccess$: Observable<boolean>;
  importMembersError$: Observable<any>;
  importedMembers$: Observable<User[]>;
  membersToImport$: Observable<any[]>;
  importMembersNonBlockingError$: Observable<any>;

  subscriptions: Subscription[] = [];
  teamId: string;

  constructor(
    private store$: Store<fromRoot.State & fromMembers.State>,
    private translateService: TranslateService,
    public ref: DialogRef
  ) {
    this.teamId = this.ref.data.teamId;
    this.identity$ = this.store$.select(fromRoot.getActiveIdentity);

    this.importCSVProcessing$ = this.store$.select(fromMembers.getImportCSVProcessing);
    this.checkContentFileProcessing$ = this.store$.select(fromMembers.getCheckContentFileProcessing);
    this.checkContentFileError$ = this.store$.select(fromMembers.getCheckContentFileError);
    this.importMembersProcessing$ = this.store$.select(fromMembers.getImportMembersProcessing);
    this.importMembersSuccess$ = this.store$.select(fromMembers.getImportMembersSuccess);
    this.importedMembers$ = this.store$.select(fromMembers.getImportedMembers);
    this.membersToImport$ = this.store$.select(fromMembers.getMembersToImport);
    this.importMembersError$ = this.store$.select(fromMembers.getImportMembersError);
    this.importMembersNonBlockingError$ = this.store$.select(fromMembers.getImportMembersNonBlockingError);
  }

  membersCSVTemplateOptions: any = {
    fieldSeparator: ',',
    filename: 'template',
    showLabels: true, // the first row will be the headers
    showTitle: false,
    useTextFile: false,
    useKeysAsHeaders: false,
  };

  csvHeaders = {
    email: 'CSV_TEMPLATE_MEMBERS_EMAIL',
    authentication_mode: 'CSV_TEMPLATE_MEMBERS_AUTHENTICATION_MODE',
    groups: 'CSV_TEMPLATE_MEMBERS_GROUPS',
    firstname: 'CSV_TEMPLATE_MEMBERS_FIRSTNAME',
    lastname: 'CSV_TEMPLATE_MEMBERS_LASTNAME',
  };

  csvExporter: any;
  fileOver: boolean;
  csvFile: File;
  csvContent: string;
  csvExtension = 'csv';

  fileNotValid = false;

  importingMembers = true;
  checkingCsvContent = false;

  ngOnInit(): void {
    this.generateCsvExporter();

    this.subscriptions.push(
      this.importMembersSuccess$.subscribe(success => {
        if (success) {
          this.store$.dispatch(MembersActions.loadMembers({ teamId: this.teamId }));
        }
      })
    )
  }

  /* Add headers translations options csv */
  async buildTemplateOptions() {
    this.membersCSVTemplateOptions.headers = Object.entries(this.csvHeaders).map(([key, value]) => `"${this.translateService.instant(value)}"`);
    const { region } = await this.identity$.pipe(first(identity => !!identity)).toPromise();
    this.membersCSVTemplateOptions.fieldSeparator = Constant.europeanRegions.find(europeanRegion => region === europeanRegion) ? ';' : ',';
    this.membersCSVTemplateOptions.decimalSeparator = Constant.europeanRegions.find(europeanRegion => region === europeanRegion) ? ',' : '.';
  }

  /* Create CSV exporter */
  async generateCsvExporter() {
    await this.buildTemplateOptions();
    this.csvExporter = new ExportToCsv(this.membersCSVTemplateOptions);
  }

  /* Generate the csv member template */
  generateTemplateCSVMembers() {
    this.csvExporter.generateCsv([{}]);
  }

  /* Generate the csv member template */
  generateTemplateCSVWithExamplesMembers() {
    this.csvExporter.generateCsv([
      { username: 'john@test.com', authenticationMode: 'SSO', groups: 'Collaborator,Marketing', firstName: 'John', lastName: 'Test' },
      { username: 'steve@test.com', authenticationMode: 'SSO', groups: 'Marketing', firstName: 'Steve', lastName: 'Test' },
      { username: 'elena@test.com', authenticationMode: 'Standard', groups: 'Collaborator', firstName: 'Elena', lastName: 'Test' },
    ]);
  }

  onFileOver(over: boolean) {
    this.fileOver = over;
  }

  initImportMembers() {
    this.store$.dispatch(MembersActions.initImportMembers({ teamId: this.teamId, fileMembers: [this.csvFile], csvContent: this.csvContent }));
  }

  async onFilesChange(files: File[]) {
    this.csvFile = files[0];
    this.csvContent = await new Response(files[0]).text()
  }

  downloadContentFileErrorReport() {
    this.checkContentFileError$.pipe(take(1)).subscribe((error) => {
      const { date, details } = error;
      this.downloadReport(date, details);
    });
  }

  downloadImportErrorReport() {
    this.importMembersError$.pipe(take(1)).subscribe((error) => {
      const { date, details } = error;
      this.downloadReport(date, details);
    });
  }

  downloadNonBlockingErrorReport() {
    this.importMembersNonBlockingError$.pipe(take(1)).subscribe((error) => {
      const { date, details } = error;
      this.downloadReport(date, details);
    });
  }

  downloadReport(date: string, content: string) {
    const element = document.createElement('a');
    const fileName = date + ' - ' + this.translateService.instant('IMPORT_MEMBERS_ERROR_REPORT_FILE_NAME');
    const fileType = 'text/plain';
    element.setAttribute('href', `data:${fileType};charset=utf-8,${encodeURIComponent(content)}`);
    element.setAttribute('download', fileName);
    var event = new MouseEvent('click');
    element.dispatchEvent(event);
  }

  resetFileSelection() {
    this.csvFile = null;
    if (this.inputFile) {
      this.inputFile.reset();
    }
  }

  resetEverything() {
    this.resetFileSelection();
    this.store$.dispatch(MembersActions.resetImportMembers());
  }

  closeModal() {
    this.resetEverything();
    this.ref.close();
  }

  ngOnDestroy(): void {
    this.resetFileSelection();
    this.subscriptions.map(subscription => subscription.unsubscribe());
    this.subscriptions.length = 0;
  }
}
